import { useCallback, useState } from "react";

/**
 * Tracks which drop zone the pointer is currently over during an HTML5 drag,
 * so a board can show the user where the card will land.
 *
 * Zones are identified by any stable string. `getZoneProps(id)` returns the
 * drag handlers to spread on the drop target; `activeZone` is the id under the
 * pointer, or null when the drag is outside every zone. The zone's own `onDrop`
 * must call `clearZone()` — the hook deliberately leaves that handler alone so
 * it never shadows the caller's drop logic.
 */
export default function useDropHighlight() {
  const [activeZone, setActiveZone] = useState(null);

  const clearZone = useCallback(() => setActiveZone(null), []);

  const getZoneProps = useCallback(
    (zoneId) => ({
      onDragEnter: (e) => {
        e.preventDefault();
        setActiveZone(zoneId);
      },
      onDragOver: (e) => {
        /* preventDefault is what marks the element as a valid drop target */
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
        setActiveZone((prev) => (prev === zoneId ? prev : zoneId));
      },
      onDragLeave: (e) => {
        /* dragleave also fires when the pointer moves onto a child element —
           only drop the highlight when it has really left the zone */
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setActiveZone((prev) => (prev === zoneId ? null : prev));
      },
    }),
    []
  );

  return { activeZone, getZoneProps, clearZone };
}
